import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';

const TherapistSettings: React.FC = () => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState({
    account: {
      licenseId: '',
      practiceName: '',
      secureEmail: user?.email || '',
      apiKey: ''
    },
    clinicalTools: {
      automatedReports: true,
      reportFrequency: 'weekly',
      reportFormat: 'pdf',
      autoApproveInvites: false,
      riskAlerts: 'email'
    },
    privacy: {
      hipaaConsent: true,
      dataRetention: '12',
      researchData: false
    },
    notifications: {
      riskAlerts: 'email',
      newMessages: true,
      reportReady: true
    },
    appearance: {
      dashboardLayout: 'detailed',
      theme: theme,
      visualization: 'standard'
    }
  });

  if (!user) {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <p>Please log in to access settings.</p>
        </div>
      </PageContainer>
    );
  }

  const handleSettingChange = (category: string, setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: value
      }
    }));
  };

  const handleSave = () => {
    alert('Settings saved successfully!');
  };

  const handleAuditTrail = () => {
    alert('Audit trail viewer coming soon');
  };

  return (
    <PageContainer>
      <PageHeader 
        title="Therapist Settings" 
        emoji="🩺"
        description="Professional controls, data governance, and clinical tool configuration"
      />

      {/* Account Section */}
      <PageSection title="Account" emoji="👤">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional License ID
            </label>
            <input
              type="text"
              value={settings.account.licenseId}
              onChange={(e) => handleSettingChange('account', 'licenseId', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your license number"
            />
            <div className="flex items-center mt-2">
              <span className="text-green-600 text-sm">✓ Verified Professional</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Practice or Clinic Name
            </label>
            <input
              type="text"
              value={settings.account.practiceName}
              onChange={(e) => handleSettingChange('account', 'practiceName', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your practice name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secure Email for Report Delivery
            </label>
            <input
              type="email"
              value={settings.account.secureEmail}
              onChange={(e) => handleSettingChange('account', 'secureEmail', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <input
              type="password"
              value={settings.account.apiKey}
              onChange={(e) => handleSettingChange('account', 'apiKey', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="For secure export or integrations"
            />
          </div>
        </div>
      </PageSection>

      {/* Clinical Tools */}
      <PageSection title="Clinical Tools" emoji="🩺">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Automated Reports</label>
              <p className="text-sm text-gray-600">Enable/disable automated reports</p>
            </div>
            <input
              type="checkbox"
              checked={settings.clinicalTools.automatedReports}
              onChange={(e) => handleSettingChange('clinicalTools', 'automatedReports', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Report Frequency
            </label>
            <select
              value={settings.clinicalTools.reportFrequency}
              onChange={(e) => handleSettingChange('clinicalTools', 'reportFrequency', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Report Format
            </label>
            <select
              value={settings.clinicalTools.reportFormat}
              onChange={(e) => handleSettingChange('clinicalTools', 'reportFormat', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Auto-Approve Patient Invites</label>
              <p className="text-sm text-gray-600">Automatically approve patient linking requests</p>
            </div>
            <input
              type="checkbox"
              checked={settings.clinicalTools.autoApproveInvites}
              onChange={(e) => handleSettingChange('clinicalTools', 'autoApproveInvites', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risk Alerts
            </label>
            <select
              value={settings.clinicalTools.riskAlerts}
              onChange={(e) => handleSettingChange('clinicalTools', 'riskAlerts', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="off">Off</option>
              <option value="email">Email only</option>
              <option value="email">Email + Dashboard</option>
            </select>
          </div>
        </div>
      </PageSection>

      {/* Privacy & Compliance */}
      <PageSection title="Privacy & Compliance" emoji="🔒">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">HIPAA Consent</label>
              <p className="text-sm text-gray-600">Required for all clinical data (locked ON)</p>
            </div>
            <input
              type="checkbox"
              checked={settings.privacy.hipaaConsent}
              disabled
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Audit Trail Viewer</label>
              <p className="text-sm text-gray-600">Access logs and activity history</p>
            </div>
            <button
              onClick={handleAuditTrail}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              View Logs
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient Data Retention Period
            </label>
            <select
              value={settings.privacy.dataRetention}
              onChange={(e) => handleSettingChange('privacy', 'dataRetention', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="6">6 months</option>
              <option value="12">12 months (default)</option>
              <option value="24">24 months</option>
              <option value="indefinite">Indefinite</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Export Anonymized Data for Research</label>
              <p className="text-sm text-gray-600">Contribute to mental health research (with disclaimer)</p>
            </div>
            <input
              type="checkbox"
              checked={settings.privacy.researchData}
              onChange={(e) => handleSettingChange('privacy', 'researchData', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
        </div>
      </PageSection>

      {/* Notifications */}
      <PageSection title="Notifications" emoji="🔔">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risk Alert Notifications
            </label>
            <select
              value={settings.notifications.riskAlerts}
              onChange={(e) => handleSettingChange('notifications', 'riskAlerts', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="none">None</option>
              <option value="email">Email</option>
              <option value="inapp">In-app</option>
              <option value="both">Email + In-app</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">New Message Notifications</label>
              <p className="text-sm text-gray-600">Notify when patients send messages</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifications.newMessages}
              onChange={(e) => handleSettingChange('notifications', 'newMessages', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Report Ready Notifications</label>
              <p className="text-sm text-gray-600">Notify when reports are generated</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notifications.reportReady}
              onChange={(e) => handleSettingChange('notifications', 'reportReady', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
        </div>
      </PageSection>

      {/* Appearance */}
      <PageSection title="Appearance" emoji="🎨">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dashboard Layout
            </label>
            <select
              value={settings.appearance.dashboardLayout}
              onChange={(e) => handleSettingChange('appearance', 'dashboardLayout', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="compact">Compact</option>
              <option value="detailed">Detailed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Theme
            </label>
            <select
              value={settings.appearance.theme}
              onChange={(e) => {
                handleSettingChange('appearance', 'theme', e.target.value);
                setTheme(e.target.value as 'light' | 'dark');
              }}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visualization Preferences
            </label>
            <select
              value={settings.appearance.visualization}
              onChange={(e) => handleSettingChange('appearance', 'visualization', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="standard">Standard</option>
              <option value="high-contrast">High Contrast</option>
              <option value="colorblind-friendly">Colorblind Friendly</option>
            </select>
          </div>
        </div>
      </PageSection>

      {/* Shared Sections */}
      <PageSection title="Shared Settings" emoji="🌐">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
            </select>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => alert('Password change functionality coming soon')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Change Password
            </button>
            <button
              onClick={() => alert('Logout functionality coming soon')}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              Logout
            </button>
          </div>

                    <div className="flex space-x-4 text-sm">
                        <a href="/legal" className="text-blue-600 hover:text-blue-800">Terms of Service</a>
                        <a href="/legal" className="text-blue-600 hover:text-blue-800">Privacy Policy</a>
                        <a href="#" className="text-blue-600 hover:text-blue-800">Support</a>
                    </div>
        </div>
      </PageSection>

      {/* Save Button */}
      <PageSection title="Save Changes" emoji="💾">
        <div className="flex justify-end space-x-4">
          <button 
            onClick={() => window.history.back()}
            className="px-6 py-2 border border-gray-300 text-gray-900 bg-white rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default TherapistSettings; 